/**
 * Product Favorites Functionality
 * Handles adding/removing products from customer favorites
 */

class ProductFavorites {
  constructor() {
    this.init();
  }

  init() {
    // Initialize favorites functionality when DOM is loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Bind click events to all favorite buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.fav-product-btn') || e.target.closest('.favorite-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFavorite(e.target.closest('.fav-product-btn') || e.target.closest('.favorite-btn'));
      }
    });

    // Load existing favorites state
    this.loadFavoritesState();
    
    // Check for and apply any stored favorites from before login
    this.applyStoredFavorites();
  }

  toggleFavorite(button) {
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const vendorName = button.dataset.vendor || button.dataset.vendorName;
    
    // Check if customer is logged in
    const isLoggedIn = window.Shopify?.customer?.email || 
                      document.querySelector('meta[name="customer-email"]')?.content ||
                      document.body.classList.contains('customer-logged-in');
    
    // If not logged in, show Klaviyo modal with pending favorite data
    if (!isLoggedIn) {
      const pendingFavoriteData = {
        type: 'product',
        productId: productId,
        productTitle: productTitle,
        vendorName: vendorName
      };
      this.openKlaviyoModal(pendingFavoriteData);
      return;
    }
    const vendorFavorited = button.dataset.vendorFavorited === 'true';
    const productFavorited = button.dataset.productFavorited === 'true';
    const heartEmpty = button.querySelector('.heart-empty');
    const heartFilled = button.querySelector('.heart-filled');
    
    if (!productId) return;

    // Check if this is a main product page button (single SVG)
    const isMainProductButton = button.classList.contains('favorite-btn');
    const heartSvg = button.querySelector('svg');

    // If product is already favorited, remove it
    if (productFavorited) {
      if (isMainProductButton) {
        // Update single SVG to outline heart
        this.updateHeartIcon(heartSvg, false);
      } else {
        // Update dual heart system
        heartFilled.classList.add('hidden');
        heartEmpty.classList.remove('hidden');
      }
      button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Remove', 'Add'));
      button.dataset.productFavorited = 'false';
      this.removeFromFavorites(productId);
      return;
    }

    // If vendor is already favorited, just add the product
    if (vendorFavorited) {
      if (isMainProductButton) {
        // Update single SVG to filled heart
        this.updateHeartIcon(heartSvg, true);
      } else {
        // Update dual heart system
        heartEmpty.classList.add('hidden');
        heartFilled.classList.remove('hidden');
      }
      button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Add', 'Remove'));
      button.dataset.productFavorited = 'true';
      this.addToFavorites(productId, productTitle);
      return;
    }

    // If vendor is not favorited, check if this is main product page
    if (isMainProductButton) {
      // On main product page, directly add the product to favorites
      this.updateHeartIcon(heartSvg, true);
      button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Add', 'Remove'));
      button.dataset.productFavorited = 'true';
      this.addToFavorites(productId, productTitle);
    } else {
      // On collection pages, show modal for vendor/product selection
      this.showFavoritesModal(button, productTitle, vendorName);
    }
  }

  updateHeartIcon(svgElement, isFilled) {
    if (!svgElement) return;
    
    if (isFilled) {
      // Change to filled heart
      svgElement.setAttribute('fill', 'currentColor');
      svgElement.setAttribute('stroke', 'none');
    } else {
      // Change to outline heart
      svgElement.setAttribute('fill', 'none');
      svgElement.setAttribute('stroke', 'currentColor');
    }
  }

  addToFavorites(productId, productTitle = null) {
    // Get current favorites from localStorage
    let favorites = this.getFavorites();
    
    // Add product if not already in favorites
    if (!favorites.includes(productId)) {
      favorites.push(productId);
      this.saveFavorites(favorites);
    }

    // Optional: Send to server/Shopify (implement based on your backend setup)
    this.syncWithServer('add', productId, productTitle);
  }

  removeFromFavorites(productId) {
    // Get current favorites from localStorage
    let favorites = this.getFavorites();
    
    // Remove product from favorites
    favorites = favorites.filter(id => id !== productId);
    this.saveFavorites(favorites);

    // Optional: Send to server/Shopify (implement based on your backend setup)
    this.syncWithServer('remove', productId);
  }

  getFavorites() {
    try {
      const favorites = localStorage.getItem('product_favorites');
      return favorites ? JSON.parse(favorites) : [];
    } catch (e) {
      console.error('Error reading favorites from localStorage:', e);
      return [];
    }
  }

  saveFavorites(favorites) {
    try {
      localStorage.setItem('product_favorites', JSON.stringify(favorites));
    } catch (e) {
      console.error('Error saving favorites to localStorage:', e);
    }
  }

  loadFavoritesState() {
    // Note: The server-side Liquid code already sets the correct initial state
    // based on customer tags. This method now only syncs localStorage with 
    // the server-side state for consistency.
    
    const serverFavorites = [];
    
    // Collect products that are server-side favorited (filled hearts visible)
    document.querySelectorAll('.fav-product-btn, .favorite-btn').forEach(button => {
      const productId = button.dataset.productId;
      const heartFilled = button.querySelector('.heart-filled');
      
      // For collection cards, check if heart is filled (not hidden)
      if (heartFilled && !heartFilled.classList.contains('hidden')) {
        serverFavorites.push(productId);
      }
      
      // For main product page, check if product is marked as favorited
      if (button.classList.contains('favorite-btn') && button.dataset.productFavorited === 'true') {
        serverFavorites.push(productId);
        // Update the heart icon to filled state
        const heartSvg = button.querySelector('svg');
        this.updateHeartIcon(heartSvg, true);
      }
    });
    
    // Update localStorage to match server state
    this.saveFavorites(serverFavorites);
  }

  async syncWithServer(action, productId, productTitle = null) {
    // Send webhook to tag or untag customer with favorite product
    const endpoint = 'https://flow-webhooks.k8s.eu.codecreationlabs.cloud/webhook/h918mh0c';
    
    try {
      // Get customer email from global Shopify object or meta tag
      const customerEmailRaw = window.Shopify?.customer?.email || 
                           document.querySelector('meta[name="customer-email"]')?.content;
      const customerEmail = this.normalizeEmail(customerEmailRaw);

      if (!customerEmail) {
        console.warn('Customer email not found for favorites webhook');
        return;
      }
      
      const tagToSend = `Fav Product ID: ${productId}`;
      const payload = {
        email: customerEmail,
        tag: tagToSend,
        remove: action === 'remove' ? 'true' : 'false',
      };
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!res.ok) {
        const errorBody = await res.text();
        console.warn('Webhook non-2xx', res.status, errorBody);
      }
      
      if (action === 'remove') {
        this.showToast('Removed from favorites');
      } else {
        this.showToast('Added to favorites');
      }
    } catch (err) {
      console.error('Webhook error', err);
      this.showToast('Something went wrong. Please try again.');
    }
  }

  openKlaviyoModal(pendingFavoriteData = null) {
    // Store pending favorite data for after signup
    if (pendingFavoriteData) {
      sessionStorage.setItem('pendingFavorite', JSON.stringify(pendingFavoriteData));
      console.log('Stored pending favorite:', pendingFavoriteData);
    }
    
    // Set up Klaviyo form submission listener if not already set
    if (!window.klaviyoFavoriteListenerSet) {
      this.setupKlaviyoFormListener();
      window.klaviyoFavoriteListenerSet = true;
    }
    
    // Trigger Klaviyo modal for non-logged-in users
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', 'UBTSnL']);
  }

  setupKlaviyoFormListener() {
    window.addEventListener("klaviyoForms", (e) => {
      if (e.detail.type === 'stepSubmit') {
        const stepName = e.detail.metaData.$step_name;
        const email = e.detail.metaData.$email;
        
        console.log('Klaviyo form step submitted:', stepName, 'Email:', email);
        
        // Check if this is a completion step (one-time code, email opt-in, etc.)
        if (stepName && email && (
          stepName.toLowerCase() === 'one-time code' || 
          stepName.toLowerCase() === 'email opt-in' ||
          stepName.toLowerCase().includes('opt-in')
        )) {
          console.log('Triggering post-signup favorite for step:', stepName);
          this.handlePostSignupFavorite(email);
        }
      }
    });
  }

  async handlePostSignupFavorite(email) {
    const pendingFavoriteData = sessionStorage.getItem('pendingFavorite');
    if (!pendingFavoriteData) {
      console.log('No pending favorite data found');
      return;
    }

    try {
      const favoriteData = JSON.parse(pendingFavoriteData);
      console.log('Processing pending favorite after signup:', favoriteData);

      // Store in localStorage with email for later when they log in
      this.storeFavoriteForLater(email, favoriteData);
      
      // Show message to user
      this.showToast(`We'll add ${favoriteData.type === 'product' ? favoriteData.productTitle : favoriteData.vendorName} to your favorites when you sign in!`);

      // Clear the pending favorite data
      sessionStorage.removeItem('pendingFavorite');
      console.log('Pending favorite stored for later login');

    } catch (error) {
      console.error('Error processing pending favorite:', error);
    }
  }

  storeFavoriteForLater(email, favoriteData) {
    const normalizedEmail = this.normalizeEmail(email);
    if (!normalizedEmail) {
      console.warn('Unable to store favorite without a valid email');
      return;
    }

    const storedFavorites = JSON.parse(localStorage.getItem('pendingFavorites') || '{}');

    if (!storedFavorites[normalizedEmail]) {
      storedFavorites[normalizedEmail] = [];
    }

    storedFavorites[normalizedEmail].push({
      ...favoriteData,
      timestamp: Date.now(),
    });

    localStorage.setItem('pendingFavorites', JSON.stringify(storedFavorites));
    console.log('Stored favorite for later:', normalizedEmail, favoriteData);
  }

  async applyStoredFavorites() {
    const customerEmailRaw = window.Shopify?.customer?.email || 
                         document.querySelector('meta[name="customer-email"]')?.content;
    const customerEmail = this.normalizeEmail(customerEmailRaw);

    if (!customerEmail) {
      console.log('User not logged in, skipping stored favorites check');
      return;
    }

    const sessionPendingFavorite = sessionStorage.getItem('pendingFavorite');
    if (sessionPendingFavorite) {
      try {
        const favoriteData = JSON.parse(sessionPendingFavorite);
        this.storeFavoriteForLater(customerEmailRaw || customerEmail, favoriteData);
        sessionStorage.removeItem('pendingFavorite');
        console.log('Migrated session favorite to localStorage for user:', customerEmail);
      } catch (error) {
        console.error('Error migrating pending favorite from sessionStorage:', error);
      }
    }

    const storedFavorites = JSON.parse(localStorage.getItem('pendingFavorites') || '{}');
    const userFavorites = storedFavorites[customerEmail] || storedFavorites[customerEmailRaw] || [];

    if (!userFavorites || userFavorites.length === 0) {
      console.log('No stored favorites found for user:', customerEmail);
      return;
    }

    console.log('Found stored favorites for user:', customerEmail, userFavorites);

    let appliedCount = 0;
    for (const favorite of userFavorites) {
      try {
        if (favorite.type === 'product') {
          await this.syncWithServer('add', favorite.productId, favorite.productTitle);
          appliedCount++;
        } else if (favorite.type === 'vendor') {
          await this.syncVendorWithServer(favorite.vendorName);
          appliedCount++;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Error applying stored favorite:', favorite, error);
      }
    }

    if (appliedCount > 0) {
      delete storedFavorites[customerEmail];
      if (customerEmailRaw && storedFavorites[customerEmailRaw]) {
        delete storedFavorites[customerEmailRaw];
      }
      localStorage.setItem('pendingFavorites', JSON.stringify(storedFavorites));

      this.showToast(`Added ${appliedCount} item${appliedCount > 1 ? 's' : ''} to your favorites!`);
      console.log(`Applied ${appliedCount} stored favorites for user:`, customerEmail);
    }
  }

  showToast(message) {
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.className = 'fixed top-4 right-4 z-50 bg-dark-blue-darker text-white text-sm font-semibold px-4 py-2 rounded-sm shadow';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  }

  showFavoritesModal(button, productTitle, vendorName) {
    const modal = document.getElementById('favorites-modal');
    const itemNameEl = document.getElementById('modal-item-name');
    const designerNameEl = document.getElementById('modal-designer-name');
    const saveItemBtn = document.getElementById('save-item-btn');
    const saveDesignerBtn = document.getElementById('save-designer-btn');
    const saveToFavoritesBtn = document.getElementById('save-to-favorites-btn');
    const closeModalBtn = document.getElementById('close-favorites-modal');

    if (!modal) return;

    // Set modal content
    itemNameEl.textContent = productTitle;
    designerNameEl.textContent = vendorName;

    // Reset selection state
    saveItemBtn.classList.remove('border-black', 'bg-pink/20', 'selected');
    saveDesignerBtn.classList.remove('border-black', 'bg-pink/20', 'selected');
    saveItemBtn.classList.add('border-gray-300', 'bg-light-gray');
    saveDesignerBtn.classList.add('border-gray-300', 'bg-light-gray');

    let selectedItem = false;
    let selectedDesigner = false;
    
    // Initialize save button as disabled
    this.updateSaveButtonState(selectedItem, selectedDesigner);

    // Handle item selection (checkbox behavior)
    saveItemBtn.onclick = (e) => {
      e.preventDefault();
      selectedItem = !selectedItem;
      
      if (selectedItem) {
        saveItemBtn.classList.remove('border-gray-300', 'bg-light-gray');
        saveItemBtn.classList.add('border-black', 'bg-pink/20', 'selected');
      } else {
        saveItemBtn.classList.add('border-gray-300', 'bg-light-gray');
        saveItemBtn.classList.remove('border-black', 'bg-pink/20', 'selected');
      }
      this.updateSaveButtonState(selectedItem, selectedDesigner);
    };

    // Handle designer selection (checkbox behavior)
    saveDesignerBtn.onclick = (e) => {
      e.preventDefault();
      selectedDesigner = !selectedDesigner;
      
      if (selectedDesigner) {
        saveDesignerBtn.classList.remove('border-gray-300', 'bg-light-gray');
        saveDesignerBtn.classList.add('border-black', 'bg-pink/20', 'selected');
      } else {
        saveDesignerBtn.classList.add('border-gray-300', 'bg-light-gray');
        saveDesignerBtn.classList.remove('border-black', 'bg-pink/20', 'selected');
      }
      this.updateSaveButtonState(selectedItem, selectedDesigner);
    };

    // Handle save button
    saveToFavoritesBtn.onclick = () => {
      if (selectedItem || selectedDesigner) {
        this.processFavoriteSelection(button, selectedItem, selectedDesigner, productTitle, vendorName);
        this.hideModal();
      }
    };

    // Handle close button
    closeModalBtn.onclick = () => this.hideModal();

    // Handle backdrop click
    modal.onclick = (e) => {
      if (e.target === modal) this.hideModal();
    };

    // Show modal
    modal.classList.remove('hidden');
    modal.classList.add('flex');
  }

  hideModal() {
    const modal = document.getElementById('favorites-modal');
    if (modal) {
      modal.classList.add('hidden');
      modal.classList.remove('flex');
    }
  }

  updateSaveButtonState(selectedItem, selectedDesigner) {
    const saveBtn = document.getElementById('save-to-favorites-btn');
    if (selectedItem || selectedDesigner) {
      saveBtn.disabled = false;
      saveBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      saveBtn.classList.add('hover:bg-teal-800');
    } else {
      saveBtn.disabled = true;
      saveBtn.classList.add('opacity-50', 'cursor-not-allowed');
      saveBtn.classList.remove('hover:bg-teal-800');
    }
  }

  processFavoriteSelection(button, selectedItem, selectedDesigner, productTitle, vendorName) {
    const productId = button.dataset.productId;
    const heartEmpty = button.querySelector('.heart-empty');
    const heartFilled = button.querySelector('.heart-filled');
    const isMainProductButton = button.classList.contains('favorite-btn');
    const heartSvg = button.querySelector('svg');

    if (selectedItem) {
      // Update visual state for product
      if (isMainProductButton) {
        // Update single SVG to filled heart
        this.updateHeartIcon(heartSvg, true);
      } else {
        // Update dual heart system
        heartEmpty.classList.add('hidden');
        heartFilled.classList.remove('hidden');
      }
      button.setAttribute('aria-label', button.getAttribute('aria-label').replace('Add', 'Remove'));
      button.dataset.productFavorited = 'true';
      
      // Add to favorites and send webhook
      this.addToFavorites(productId, productTitle);
    }

    if (selectedDesigner) {
      // Send vendor favorite webhook
      this.syncVendorWithServer(vendorName);
      button.dataset.vendorFavorited = 'true';
    }
  }

  async syncVendorWithServer(vendorName, remove = false) {
    const endpoint = 'https://flow-webhooks.k8s.eu.codecreationlabs.cloud/webhook/h918mh0c';
    
    try {
      const customerEmailRaw = window.Shopify?.customer?.email || 
                           document.querySelector('meta[name="customer-email"]')?.content;
      const customerEmail = this.normalizeEmail(customerEmailRaw);
      
      if (!customerEmail) {
        console.warn('Customer email not found for vendor favorites webhook');
        return;
      }
      
      const tagToSend = `Fav Vendor: ${vendorName}`;
      
      const payload = {
        email: customerEmail,
        tag: tagToSend,
        remove: remove ? 'true' : 'false',
      };
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!res.ok) {
        const errorBody = await res.text();
        console.warn('Vendor webhook non-2xx', res.status, errorBody);
      } else {
        this.showToast(remove ? `Removed ${vendorName} from favorites` : `Added ${vendorName} to favorites`);
        // Reload page after a short delay to update the UI
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err) {
      console.error('Vendor webhook error', err);
      this.showToast('Something went wrong. Please try again.');
    }
  }

  async removeVendorFavorite(vendorName) {
    await this.syncVendorWithServer(vendorName, true);
  }

  async sendPostSignupProductWebhook(email, productId, productTitle) {
    const endpoint = 'https://flow-webhooks.k8s.eu.codecreationlabs.cloud/webhook/h918mh0c';
    
    try {
      const tagToSend = `Fav Product ID: ${productId}`;
      console.log('Post-signup product tag being sent to webhook:', tagToSend);
      console.log('Email:', email, 'Product ID:', productId, 'Product Title:', productTitle);
      
      const payload = {
        email: email,
        tag: tagToSend,
      };
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!res.ok) {
        console.warn('Post-signup product webhook non-2xx', res.status);
      }
    } catch (err) {
      console.error('Post-signup product webhook error', err);
      throw err; // Re-throw to handle in calling function
    }
  }

  async sendPostSignupVendorWebhook(email, vendorName) {
    const endpoint = 'https://flow-webhooks.k8s.eu.codecreationlabs.cloud/webhook/h918mh0c';
    
    try {
      const tagToSend = `Fav Vendor: ${vendorName}`;
      console.log('Post-signup vendor tag being sent to webhook:', tagToSend);
      console.log('Email:', email, 'Vendor Name:', vendorName);
      
      const payload = {
        email: email,
        tag: tagToSend,
        remove: 'false',
      };
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit',
      });
      
      if (!res.ok) {
        const errorBody = await res.text();
        console.warn('Post-signup vendor webhook non-2xx', res.status, errorBody);
      }
    } catch (err) {
      console.error('Post-signup vendor webhook error', err);
      throw err; // Re-throw to handle in calling function
    }
  }

  normalizeEmail(email) {
    if (typeof email !== 'string') return '';
    return email.trim().toLowerCase();
  }
}

// Initialize the favorites functionality
window.ProductFavorites = new ProductFavorites();
